import type { Coin } from "./coins";
import { formatWeight, signed } from "./format";
import { type Dict, interpolate, type Labels } from "./labels";

export type CatalogEntry = {
	slug: string;
	name: string;
	concept: string;
	cost: number;
	quantity: number;
	magic_item: boolean;
	custom: boolean;
	damage: string | null;
	weight_in_load: number | null;
	weight_in_grams: number | null;
	thumbnail_url: string | null;
	url: string;
	description_html: string;
	parts: { name: string; quantity: number }[];
	damage_type: string;
	bonus_ca: number | null;
	bonus_ba: number | null;
	bonus_damage: number | null;
	shoot_range: number | null;
	throw_range: number | null;
	increases_load_by: number | null;
	two_handed: boolean;
};

// A page of the catalog: `page` of `pages`, the numbered pagination the old drawer has.
export type CatalogPage = { items: CatalogEntry[]; next: number | null; page: number; pages: number };

// The two chip groups are toggles: none on means all, as both on does.
export type CatalogFilters = { name: string; concept: string | null; magic: ("none" | "only")[]; origin: ("official" | "mine")[] };

export const CONCEPTS = ["weapon", "armor", "shield", "misc", "container", "vehicle", "kit"];

export function searchParams(filters: CatalogFilters, page: number): URLSearchParams {
	const params = new URLSearchParams();
	if (filters.name.trim()) params.set("name", filters.name.trim());
	if (filters.concept) params.append("concepts[]", filters.concept);
	for (const magic of filters.magic) params.append("magicality[]", magic);
	for (const origin of filters.origin) params.append("origin[]", origin);
	if (page > 1) params.set("page", String(page));
	return params;
}

// The purse in its biggest whole coin, rounded, as the old sheet words it: 4150 is "42 PO".
export function roughCost(copper: number, coins: Dict): string {
	if (copper >= 100) return `${Math.round(copper / 100)} ${coins.gp}`;
	if (copper >= 10) return `${Math.round(copper / 10)} ${coins.sp}`;
	return `${Math.max(0, copper)} ${coins.cp}`;
}

// Copper as the books write it, biggest coin first: 250 is "2 PO 5 PP".
export function formatCost(copper: number, coins: Dict): string {
	if (copper <= 0) return `0 ${coins.cp}`;
	const parts: [number, string][] = [
		[Math.floor(copper / 100), coins.gp],
		[Math.floor((copper % 100) / 10), coins.sp],
		[copper % 10, coins.cp],
	];
	return parts
		.filter(([amount]) => amount > 0)
		.map(([amount, unit]) => `${amount} ${unit}`)
		.join(" ");
}

export const purseInCopper = (sheet: { money_gp: number; money_sp: number; money_cp: number }) => sheet.money_gp * 100 + sheet.money_sp * 10 + sheet.money_cp;

export const toggled = <T>(list: T[], value: T): T[] => (list.includes(value) ? list.filter((entry) => entry !== value) : [...list, value]);

// Copper for `quantity` units of an entry sold in packs of `pack`, rounded up: what the server charges, and the old drawer's rule.
export const totalFor = (cost: number, pack: number, quantity: number): number =>
	quantity > 0 ? Math.ceil((Math.max(0, cost) * quantity) / Math.max(1, pack)) : 0;

export function splitCoins(copper: number): Record<Coin, number> {
	return { gp: Math.floor(copper / 100), sp: Math.floor((copper % 100) / 10), cp: copper % 10 };
}

export const joinCoins = ({ gp, sp, cp }: Record<Coin, number>): number => gp * 100 + sp * 10 + cp;

// The page links: the first, the last and a window around the current one, with a gap marked where pages are left out.
export function pageNumbers(page: number, pages: number): (number | "…")[] {
	const shown = [...new Set([1, page - 1, page, page + 1, pages].filter((n) => n >= 1 && n <= pages))].sort((a, b) => a - b);
	if (pages <= 5) return Array.from({ length: pages }, (_, index) => index + 1);
	return shown.flatMap((n, index) => (index > 0 && n - (shown[index - 1] as number) > 1 ? ["…" as const, n] : [n]));
}

// An entry's one-line facts, as the rows and the open row's first line word them.
export function entryFacts(entry: CatalogEntry, labels: Pick<Labels, "catalog" | "item" | "damage_types">, locale: string): string[] {
	const { catalog, item } = labels;
	const facts: string[] = [];
	if (entry.damage) facts.push([entry.damage, entry.damage_type !== "none" && labels.damage_types[entry.damage_type]].filter(Boolean).join(" "));
	if (entry.shoot_range) facts.push(interpolate(catalog.shoot as string, { n: entry.shoot_range }));
	if (entry.throw_range) facts.push(interpolate(catalog.throw as string, { n: entry.throw_range }));
	if (entry.two_handed) facts.push(catalog.two_handed as string);
	if (entry.bonus_ca !== null) facts.push(interpolate(item.preview_ca as string, { n: signed(entry.bonus_ca) }));
	if (entry.bonus_ba) facts.push(interpolate(item.preview_ba as string, { n: signed(entry.bonus_ba) }));
	if (entry.bonus_damage) facts.push(interpolate(item.preview_damage as string, { n: signed(entry.bonus_damage) }));
	if (entry.increases_load_by) facts.push(interpolate(item.preview_capacity as string, { n: entry.increases_load_by }));
	if (entry.weight_in_grams) facts.push(formatWeight(entry.weight_in_grams, locale));
	else if (entry.weight_in_load) facts.push(interpolate(item.preview_load as string, { n: entry.weight_in_load }));
	if (entry.quantity > 1) facts.push(interpolate(catalog.pack as string, { n: entry.quantity }));
	return facts;
}
